/**
 * Blackwaxx&Co announcement bar: a continuous horizontal ticker of the store's messages.
 *
 * Markup is two identical tracks placed edge to edge; the whole pair translates left by exactly one track's width in
 * a linear, looping animation, so the seam is invisible and it reads as one endless line. Duration is derived from
 * the track's own measured width and a pixels-per-second speed setting, so the perceived speed stays constant
 * whether there's one short message or three long ones. Pauses on hover, on keyboard focus, while the tab is hidden,
 * and stays still entirely for prefers-reduced-motion.
 */
class BwAnnouncement extends HTMLElement {
  #resizeObserver;

  connectedCallback() {
    this.viewport = this.querySelector('.bw-announce__viewport');
    this.track = this.querySelector('[data-bw-track]');
    this.speed = Math.max(10, Number(this.dataset.speed) || 50);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.reduced) {
      this.classList.add('is-static');
      return;
    }

    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.track);
    this.#measure();

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #measure() {
    const width = this.track.getBoundingClientRect().width;
    if (!width) return;
    const duration = width / this.speed;
    this.style.setProperty('--bw-marquee-width', `${width}px`);
    this.style.setProperty('--bw-marquee-duration', `${duration}s`);
    this.classList.add('is-ready');
  }

  #onVisibility = () => {
    this.classList.toggle('is-paused', document.hidden);
  };

  #pause = () => this.classList.add('is-paused');
  #resume = () => this.classList.remove('is-paused');
}

if (!customElements.get('bw-announcement')) customElements.define('bw-announcement', BwAnnouncement);
